import React, { useState } from 'react';
import { auth } from '../firebase';
import axios from 'axios';
import { MEMBERSHIP_TIER_BENEFITS } from '../membershipTierBenefits';
import { Capacitor } from '@capacitor/core';

const TierBenefitsList = ({ tier, color }) => (
  <div style={{ fontSize: '14px', color, marginBottom: '16px' }}>
    {MEMBERSHIP_TIER_BENEFITS[tier].map((line, index, lines) => (
      <div
        key={line}
        style={index < lines.length - 1 ? { marginBottom: '8px' } : undefined}
      >
        {line}
      </div>
    ))}
  </div>
);

const SubscriptionModal = ({ isOpen, onClose, currentMembershipTier = 'free', subscriptionData = {} }) => {
  const [loading, setLoading] = useState(null);
  // On the native iOS app we can't sell a digital subscription in-app
  // (App Store Guideline 3.1.1). The tier info still shows, but the buy/
  // change/cancel actions are replaced with a link to manage membership on
  // the website. Web behavior is unchanged.
  const isNative = Capacitor.isNativePlatform();

  if (!isOpen) return null;

  // Check if subscription is cancelled
  const isSubscriptionCancelled = subscriptionData.cancelAtPeriodEnd === true;
  const cancelDate = subscriptionData.cancelAt;
  
  // Safely format the cancel date, handling different formats
  let formattedCancelDate = null;
  if (cancelDate) {
    try {
      // Check if it's a Firestore timestamp with seconds property
      if (cancelDate.seconds) {
        formattedCancelDate = new Date(cancelDate.seconds * 1000).toLocaleDateString();
      } 
      // Check if it's a string or can be parsed as a date
      else {
        const date = new Date(cancelDate);
        if (!isNaN(date.getTime())) {
          formattedCancelDate = date.toLocaleDateString();
        }
      }
    } catch (error) {
      console.error('Error formatting cancel date:', error);
      formattedCancelDate = null;
    }
  }

  const handleMembershipUpgrade = async (membershipTier) => {
    if (!auth.currentUser) {
      alert('Please sign in to upgrade your membership.');
      return;
    }

    setLoading(membershipTier);
    
    try {
      const idToken = await auth.currentUser.getIdToken();
      
      const response = await axios.post('https://us-central1-pedalxchange.cloudfunctions.net/createMembershipCheckout', {
        membershipTier: membershipTier,
        userId: auth.currentUser.uid
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Error creating checkout session. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleUpgrade9V = () => handleMembershipUpgrade('premium_basic');
  const handleUpgrade18V = () => {
    // If user already has a subscription (9V), use upgrade function instead of checkout
    if (currentMembershipTier === 'premium_basic') {
      handleMembershipChange('premium_plus');
    } else {
      // For free users, use the regular checkout flow
      handleMembershipUpgrade('premium_plus');
    }
  };
  
  // Handle downgrade/cancellation
  const handleMembershipChange = async (newTier) => {
    console.log('🔄 handleMembershipChange called with:', newTier);
    
    if (!auth.currentUser) {
      alert('Please sign in to change your membership.');
      return;
    }
    
    setLoading(newTier);
    
    try {
      const idToken = await auth.currentUser.getIdToken();
      console.log('✅ Got auth token');
      
      let response;
      if (newTier === 'free') {
        // Cancel subscription
        console.log('📤 Calling cancelSubscription API...');
        response = await axios.post('https://us-central1-pedalxchange.cloudfunctions.net/cancelSubscription', {
          userId: auth.currentUser.uid
        }, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('📥 Cancel API response:', response.data);
        
        if (response.data.success) {
          const cancelMessage = response.data.cancelAtFormatted 
            ? `Your subscription has been scheduled for cancellation. You'll continue to have access until ${response.data.cancelAtFormatted}.`
            : 'Your subscription has been scheduled for cancellation at the end of your current billing period.';
          alert(cancelMessage);
          onClose(); // Close the modal
          window.location.reload(); // Reload to show updated status
        }
      } else if (newTier === 'premium_basic') {
        // Downgrade to 9V
        response = await axios.post('https://us-central1-pedalxchange.cloudfunctions.net/downgradeSubscription', {
          userId: auth.currentUser.uid,
          newMembershipTier: newTier
        }, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.success) {
          alert('Your subscription has been downgraded to 9V membership. The change is effective immediately.');
          onClose(); // Close the modal
          // Optionally refresh the page to update the UI
          window.location.reload();
        }
      } else if (newTier === 'premium_plus') {
        // Upgrade to 18V
        response = await axios.post('https://us-central1-pedalxchange.cloudfunctions.net/upgradeSubscription', {
          userId: auth.currentUser.uid,
          newMembershipTier: newTier
        }, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data.success) {
          alert('Your subscription has been upgraded to 18V membership. You were charged the prorated difference and the change is effective immediately.');
          onClose(); // Close the modal
          // Optionally refresh the page to update the UI
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error changing membership:', error);
      let errorMessage = 'Error processing request. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      alert(errorMessage);
    } finally {
      setLoading(null);
    }
  };

  // Handle backdrop click to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '24px' }}>
          {/* Header — title centered; close button pinned top-right so it
              doesn't shift the title off-center */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0, textAlign: 'center', padding: '0 40px' }}>
              Choose Your Membership
            </h2>
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                fontSize: '28px',
                color: '#6B7280',
                cursor: 'pointer',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>

          {/* Native: surface the "manage on the web" note up top, right under
              the header, so it's clearly discoverable before the tier cards. */}
          {isNative && (
            <div style={{ textAlign: 'center', fontSize: '14px', color: '#4B5563', marginBottom: '24px', lineHeight: 1.5 }}>
              To start, change, or cancel a paid membership, visit your profile page on{' '}
              <a
                href="https://pedalxchange.com/profile#membership-section"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#5A9ECB', fontWeight: 600, textDecoration: 'underline' }}
              >
                pedalxchange.com
              </a>.
            </div>
          )}

          {/* Membership Options */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '20px',
            marginBottom: '20px'
          }}>
            
            {/* Free Tier */}
            <div style={{ 
              border: '1px solid #E5E7EB', 
              borderRadius: '8px', 
              padding: '20px',
              backgroundColor: '#F9FAFB'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
                  Free Tier
                </h3>
                <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                  $0<span style={{ fontSize: '16px', fontWeight: 'normal' }}>/month</span>
                </p>
              </div>
              <TierBenefitsList tier="free" color="#6B7280" />
              <div style={{ textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
                {currentMembershipTier === 'free' && !isSubscriptionCancelled ? 'Current Plan' : ''}
              </div>
              {!isNative && currentMembershipTier !== 'free' && !isSubscriptionCancelled && (
                <button
                  onClick={() => handleMembershipChange('free')}
                  disabled={loading}
                  style={{
                    width: '100%',
                    backgroundColor: loading ? '#9CA3AF' : '#E5E7EB',
                    color: loading ? 'white' : '#374151',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    marginTop: '16px'
                  }}
                >
                  {loading === 'free' ? 'Canceling...' : 'Cancel Subscription'}
                </button>
              )}
            </div>

            {/* 9V Membership */}
            <div style={{ 
              border: '2px solid #5A9ECB', 
              borderRadius: '8px', 
              padding: '20px',
              backgroundColor: '#EBF4FF'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#5A9ECB', margin: '0 0 8px 0' }}>
                  9 Volt Membership
                </h3>
                <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#5A9ECB', margin: 0 }}>
                  $5<span style={{ fontSize: '16px', fontWeight: 'normal' }}>/month</span>
                </p>
              </div>
              <TierBenefitsList tier="premium_basic" color="#374151" />
              {currentMembershipTier === 'premium_basic' ? (
                <div style={{ textAlign: 'center', color: '#5A9ECB', fontSize: '14px', fontWeight: '600' }}>
                  Current Plan
                  {isSubscriptionCancelled && (
                    <div style={{ color: '#DC2626', fontWeight: '600', marginTop: '8px' }}>
                      {formattedCancelDate 
                        ? `Cancelled - Ends ${formattedCancelDate}` 
                        : 'Cancelled - Ends at period end'}
                    </div>
                  )}
                </div>
              ) : isNative ? null : currentMembershipTier === 'free' || isSubscriptionCancelled ? (
                <button
                  onClick={handleUpgrade9V}
                  disabled={loading}
                  style={{
                    width: '100%',
                    backgroundColor: loading ? '#9CA3AF' : '#5A9ECB',
                    color: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {loading === 'premium_basic' ? 'Creating Checkout...' : 
                   isSubscriptionCancelled ? 'Reactivate with 9V' : 'Upgrade to 9V'}
                </button>
              ) : (
                <button
                  onClick={() => handleMembershipChange('premium_basic')}
                  disabled={loading}
                  style={{
                    width: '100%',
                    backgroundColor: loading ? '#9CA3AF' : '#5A9ECB',
                    color: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {loading === 'premium_basic' ? 'Downgrading...' : 'Downgrade to 9V'}
                </button>
              )}
            </div>

            {/* 18V Membership */}
            <div style={{ 
              border: '2px solid #C9A227', 
              borderRadius: '8px', 
              padding: '20px',
              backgroundColor: '#FEF7E8'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#C9A227', margin: '0 0 8px 0' }}>
                  18 Volt Membership
                </h3>
                <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#C9A227', margin: 0 }}>
                  $9<span style={{ fontSize: '16px', fontWeight: 'normal' }}>/month</span>
                </p>
              </div>
              <TierBenefitsList tier="premium_plus" color="#374151" />
              {currentMembershipTier === 'premium_plus' ? (
                <div style={{ textAlign: 'center', color: '#C9A227', fontSize: '14px', fontWeight: '600' }}>
                  Current Plan
                  {isSubscriptionCancelled && (
                    <div style={{ color: '#DC2626', fontWeight: '600', marginTop: '8px' }}>
                      {formattedCancelDate 
                        ? `Cancelled - Ends ${formattedCancelDate}` 
                        : 'Cancelled - Ends at period end'}
                    </div>
                  )}
                </div>
              ) : isNative ? null : (
                <button
                  onClick={handleUpgrade18V}
                  disabled={loading}
                  style={{
                    width: '100%',
                    backgroundColor: loading ? '#9CA3AF' : '#C9A227',
                    color: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {loading === 'premium_plus' ? 'Creating Checkout...' : 
                   isSubscriptionCancelled ? 'Reactivate with 18V' : 'Upgrade to 18V'}
                </button>
              )}
            </div>
          </div>




          {/* Footer — web only (native steers to the web note shown up top) */}
          {!isNative && (
            <div style={{ 
              textAlign: 'center', 
              fontSize: '12px', 
              color: '#6B7280',
              borderTop: '1px solid #E5E7EB',
              paddingTop: '16px'
            }}>
              <p style={{ margin: 0 }}>✨ Secure payment powered by Stripe</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal; 